import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { Field, SelectField } from "../components/Field";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { DataTable } from "../components/DataTable";
import { Tabs } from "../components/Tabs";
import "./groundtruth.css";

/**
 * PRODUCT_FLOW.md Stage 2 — Ground Truth Configuration.
 *
 * Three things in one place, because they are one task: define what you track
 * (pillars), give it source material (documents), and confirm what was pulled
 * out of it (claim review). The Ask Echo box sits alongside so a Comms Manager
 * can immediately check what their own approved corpus actually says.
 */

interface Pillar {
  pillarId: string;
  name: string;
  description: string;
  approvedClaimCount: number;
  pendingClaimCount: number;
}

interface DocumentRow {
  documentId: string;
  title: string;
  /** Which Narrative Pillars this document was tagged to (FR-ING-01). */
  pillarIds: string[];
  sourceType: string;
  status: "queued" | "processing" | "indexed" | "failed";
  statusDetail: string | null;
  claimCount: number;
  supersededBy: string | null;
  uploadedAt: string;
}

interface Claim {
  claimId: string;
  documentId: string;
  pillarId: string;
  text: string;
  approvalStatus: "pending" | "approved" | "rejected";
  extractionConfidence: number;
  editedFromOriginal: boolean;
  originalText: string | null;
}

interface GapStatus {
  pillarId: string;
  pillarName: string;
  severity: "ok" | "warning" | "critical";
  approvedClaimCount: number;
  gaps: { code: string; message: string }[];
  contradictionCheckAvailable: boolean;
}

const SOURCE_TYPES = [
  ["press_release", "Press release"],
  ["esg_report", "ESG report"],
  ["ir_transcript", "IR transcript"],
  ["hr_policy", "HR policy"],
  ["position_statement", "Position statement"],
  ["other", "Other"],
] as const;

/** FR-ING-02's AC — indexing status is visible, and failures offer a retry. */
function StatusChip({ status }: { status: DocumentRow["status"] }) {
  const map = {
    queued: { cls: "badge--neutral", label: "Queued" },
    processing: { cls: "badge--info", label: "Processing" },
    indexed: { cls: "badge--success", label: "Indexed" },
    failed: { cls: "badge--error", label: "Failed" },
  } as const;
  const { cls, label } = map[status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export function GroundTruthPage() {
  const { activeBrand, can } = useAuth();
  const brandId = activeBrand?.brandId;

  const [pillars, setPillars] = useState<Pillar[] | null>(null);
  const [documents, setDocuments] = useState<DocumentRow[] | null>(null);
  const [claims, setClaims] = useState<Claim[] | null>(null);
  const [gaps, setGaps] = useState<GapStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Approved claims have to be reviewable and reversible — an approval that
  // can't be undone means a bad claim is stuck in the scored baseline.
  const [claimStatus, setClaimStatus] = useState<
    "pending" | "approved" | "rejected"
  >("pending");

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const [p, d, c] = await Promise.all([
        api<{ items: Pillar[] }>(`/brands/${brandId}/pillars`),
        api<{ items: DocumentRow[] }>(`/brands/${brandId}/documents`),
        api<{ items: Claim[] }>(
          `/brands/${brandId}/claims?approvalStatus=${claimStatus}&limit=200`,
        ),
      ]);
      setPillars(p.items);
      setDocuments(d.items);
      setClaims(c.items);

      const statuses = await Promise.all(
        p.items.map((pillar) =>
          api<GapStatus>(`/pillars/${pillar.pillarId}/gap-status`).catch(
            () => null,
          ),
        ),
      );
      setGaps(statuses.filter((s): s is GapStatus => s !== null));
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not load Ground Truth.",
      );
      setPillars([]);
      setDocuments([]);
      setClaims([]);
    }
  }, [brandId, claimStatus]);

  useEffect(() => {
    void load();
  }, [load]);

  // Ingestion runs out of band, so the status chips poll while anything is in
  // flight — and stop the moment nothing is, rather than polling forever.
  const inFlight = documents?.some(
    (d) => d.status === "queued" || d.status === "processing",
  );
  useEffect(() => {
    if (!inFlight) return;
    const timer = setInterval(() => void load(), 2500);
    return () => clearInterval(timer);
  }, [inFlight, load]);

  if (!activeBrand) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">Ground Truth</h1>
        </header>
        <div className="card">
          <EmptyState
            icon="brand"
            title="No Brand selected"
            description="Choose a Brand from the switcher to manage its Ground Truth."
          />
        </div>
      </>
    );
  }

  const mayUpload = can("brand:upload_documents");
  const mayApprove = can("brand:approve_claims");
  const mayManagePillars = can("brand:manage_pillars");

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Ground Truth</h1>
        <p className="page__subtitle">
          What <strong>{activeBrand.brandName}</strong> officially says — the
          baseline every drift score is measured against.
        </p>
      </header>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      )}

      {/* FR-ING-05 — the gap banner belongs HERE, at the moment the material is
          being added, not buried in the dashboard later (PRODUCT_FLOW Stage 2 §4). */}
      {gaps
        .filter((g) => g.severity !== "ok")
        .map((gap) => (
          <div
            key={gap.pillarId}
            className={`alert ${gap.severity === "critical" ? "alert--error" : "alert--warning"}`}
            role="status"
          >
            <Icon name="alert" size={20} />
            <span>
              <strong>{gap.pillarName}:</strong>{" "}
              {gap.gaps.map((g) => g.message).join(" ")}
            </span>
          </div>
        ))}

      {/* Five stacked sections had become a long scroll. Tabs keep the page's
          structure visible and every section one click away — the upload form
          lives with Documents because adding a source and seeing its status
          are one task. */}
      <Tabs
        ariaLabel="Ground Truth sections"
        tabs={[
          {
            id: "pillars",
            label: "Narrative Pillars",
            icon: "sparkle",
            badge: pillars?.length ?? null,
            render: () => (
              <PillarSection
                pillars={pillars}
                canManage={mayManagePillars}
                brandId={activeBrand.brandId}
                onChange={load}
                onNotice={setNotice}
              />
            ),
          },
          {
            id: "documents",
            label: "Documents",
            icon: "groundTruth",
            badge: documents?.length ?? null,
            render: () => (
              <>
                {mayUpload && pillars && pillars.length > 0 && (
                  <UploadSection
                    brandId={activeBrand.brandId}
                    pillars={pillars}
                    onUploaded={load}
                    onNotice={setNotice}
                  />
                )}
                <DocumentSection
                  documents={documents}
                  pillars={pillars ?? []}
                />
              </>
            ),
          },
          {
            id: "claims",
            label: "Claims",
            icon: "check",
            badge: claims?.length ?? null,
            render: () => (
              <ClaimReviewSection
                claims={claims}
                pillars={pillars ?? []}
                brandId={activeBrand.brandId}
                canApprove={mayApprove}
                status={claimStatus}
                onStatusChange={setClaimStatus}
                onChange={load}
                onNotice={setNotice}
              />
            ),
          },
          {
            id: "ask",
            label: "Ask your Ground Truth",
            icon: "prompts",
            badge: null,
            render: () => (
              <AskEchoSection
                brandId={activeBrand.brandId}
                pillars={pillars ?? []}
              />
            ),
          },
        ]}
      />
    </>
  );
}

/* ------------------------------- Pillars ------------------------------- */

function PillarSection({
  pillars,
  canManage,
  brandId,
  onChange,
  onNotice,
}: {
  pillars: Pillar[] | null;
  canManage: boolean;
  brandId: string;
  onChange: () => Promise<void>;
  onNotice: (msg: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/brands/${brandId}/pillars`, {
        method: "POST",
        body: { name, description },
      });
      setName("");
      setDescription("");
      setOpen(false);
      onNotice(`Added the "${name}" pillar.`);
      await onChange();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not add that pillar.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card gt-section">
      <div className="gt-section__head">
        <h2 className="gt-section__title">Narrative Pillars</h2>
        {canManage && (
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setOpen((v) => !v)}
          >
            <Icon name="plus" size={16} />
            Add pillar
          </button>
        )}
      </div>

      {open && (
        <form onSubmit={create} className="gt-inlineform" noValidate>
          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}
          <Field
            label="Pillar name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ESG &amp; Net-Zero"
            required
          />
          <Field
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            help="What this pillar covers, in a sentence."
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
            }}
          >
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? "Adding…" : "Add pillar"}
            </button>
          </div>
        </form>
      )}

      {pillars === null ? (
        <div className="skeleton" style={{ height: 60 }} />
      ) : pillars.length === 0 ? (
        <EmptyState
          icon="sparkle"
          title="No Narrative Pillars yet"
          description="A pillar is a strategic topic you want tracked — ESG & Net-Zero, Executive Leadership, Financial Stability. Everything else hangs off one."
          action={
            canManage ? (
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setOpen(true)}
              >
                Add your first pillar
              </button>
            ) : undefined
          }
        />
      ) : (
        <ul className="gt-pillars">
          {pillars.map((pillar) => (
            <li key={pillar.pillarId} className="gt-pillar">
              <span className="gt-pillar__name">{pillar.name}</span>
              {pillar.description && (
                <span className="gt-pillar__desc">{pillar.description}</span>
              )}
              <span className="gt-pillar__counts">
                <span className="badge badge--success">
                  {pillar.approvedClaimCount} approved
                </span>
                {pillar.pendingClaimCount > 0 && (
                  <span className="badge badge--info">
                    {pillar.pendingClaimCount} to review
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ------------------------------- Upload -------------------------------- */

function UploadSection({
  brandId,
  pillars,
  onUploaded,
  onNotice,
}: {
  brandId: string;
  pillars: Pillar[];
  onUploaded: () => Promise<void>;
  onNotice: (msg: string) => void;
}) {
  const [pillarId, setPillarId] = useState(pillars[0]?.pillarId ?? "");
  const [sourceType, setSourceType] = useState<string>("press_release");
  const [sourceUrl, setSourceUrl] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function send(file: File | null) {
    setError(null);
    setBusy(true);
    try {
      const form = new FormData();
      form.append("pillarIds", pillarId || pillars[0]!.pillarId);
      form.append("sourceType", sourceType);
      if (file) form.append("file", file);
      else form.append("sourceUrl", sourceUrl);

      // FormData must not carry a JSON content-type, so this bypasses `api()`.
      const token = localStorage.getItem("echo.accessToken");
      const response = await fetch(`/api/v1/brands/${brandId}/documents`, {
        method: "POST",
        headers: token ? { authorization: `Bearer ${token}` } : {},
        body: form,
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(body?.error?.message ?? "Upload failed.");
      }
      onNotice(`"${body.title}" is being processed.`);
      setSourceUrl("");
      if (inputRef.current) inputRef.current.value = "";
      await onUploaded();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card gt-section">
      <div className="gt-section__head">
        <h2 className="gt-section__title">Add source material</h2>
      </div>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <div className="gt-uploadgrid">
        <SelectField
          label="Narrative Pillar"
          value={pillarId}
          onChange={(e) => setPillarId(e.target.value)}
        >
          {pillars.map((p) => (
            <option key={p.pillarId} value={p.pillarId}>
              {p.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label="Document type"
          value={sourceType}
          onChange={(e) => setSourceType(e.target.value)}
        >
          {SOURCE_TYPES.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
      </div>

      {/* DESIGN_SYSTEM §7 — "the Ground Truth upload form specifically needs a
          drag-drop zone"; this is the form a CommsManager uses most. */}
      <div
        className={`gt-dropzone${dragging ? " gt-dropzone--active" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) void send(file);
        }}
      >
        <Icon name="groundTruth" size={24} />
        <p>
          Drop a PDF, DOCX, HTML, or text file here, or{" "}
          <button
            type="button"
            className="auth__link"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
            onClick={() => inputRef.current?.click()}
          >
            browse
          </button>
          .
        </p>
        <input
          ref={inputRef}
          type="file"
          className="visually-hidden"
          accept=".pdf,.docx,.html,.htm,.txt,.md"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void send(file);
          }}
        />
        {busy && <span className="badge badge--info">Uploading…</span>}
      </div>

      <div className="gt-urlrow">
        <Field
          label="…or register a URL"
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://example.com/newsroom/press-release"
        />
        <button
          type="button"
          className="btn btn--secondary"
          disabled={!sourceUrl || busy}
          onClick={() => void send(null)}
        >
          Fetch
        </button>
      </div>
    </section>
  );
}

/* ------------------------------ Documents ------------------------------ */

function DocumentSection({
  documents,
  pillars,
}: {
  documents: DocumentRow[] | null;
  pillars: Pillar[];
}) {
  const pillarName = (id: string) =>
    pillars.find((p) => p.pillarId === id)?.name ?? "Unknown pillar";

  return (
    <section className="card gt-section" style={{ padding: 0 }}>
      <div
        className="gt-section__head"
        style={{ padding: "var(--space-6) var(--space-6) 0" }}
      >
        <h2 className="gt-section__title">Documents</h2>
      </div>

      <DataTable
        rows={documents}
        rowKey={(d) => d.documentId}
        searchPlaceholder="Search documents by title or type…"
        pageSize={10}
        empty={{
          icon: "inbox",
          title: "No documents yet",
          description:
            "Upload a press release, ESG report, or position statement to establish what this Brand officially says.",
        }}
        columns={[
          {
            key: "title",
            header: "Document",
            value: (d) => d.title,
            render: (d) => (
              <>
                {d.title}
                {d.supersededBy && (
                  <span
                    className="badge badge--neutral"
                    style={{ marginLeft: "var(--space-2)" }}
                  >
                    Superseded
                  </span>
                )}
                {d.statusDetail && (
                  <div className="gt-statusdetail">{d.statusDetail}</div>
                )}
              </>
            ),
          },
          {
            // A document's pillars are what decide which prompts and which
            // scores it can ever affect, so a Ground Truth library that doesn't
            // show them can't answer "why is this pillar thin?" — the question
            // FR-ING-05's gap detection exists to raise.
            key: "pillars",
            header: "Narrative Pillar",
            value: (d) => d.pillarIds.map(pillarName).join(" "),
            render: (d) =>
              d.pillarIds.length === 0 ? (
                <span className="muted">Untagged</span>
              ) : (
                <span className="doc__pillars">
                  {d.pillarIds.map((id) => (
                    <span className="badge badge--neutral" key={id}>
                      {pillarName(id)}
                    </span>
                  ))}
                </span>
              ),
          },
          {
            key: "type",
            header: "Type",
            value: (d) =>
              SOURCE_TYPES.find(([v]) => v === d.sourceType)?.[1] ??
              d.sourceType,
            render: (d) =>
              SOURCE_TYPES.find(([v]) => v === d.sourceType)?.[1] ??
              d.sourceType,
          },
          {
            key: "status",
            header: "Status",
            value: (d) => d.status,
            render: (d) => <StatusChip status={d.status} />,
          },
          {
            key: "claims",
            header: "Claims",
            align: "right",
            value: (d) => d.claimCount,
            render: (d) => <span className="tabular">{d.claimCount}</span>,
          },
        ]}
      />
    </section>
  );
}

/* ---------------------------- Claim review ----------------------------- */

function ClaimReviewSection({
  claims,
  pillars,
  brandId,
  canApprove,
  status,
  onStatusChange,
  onChange,
  onNotice,
}: {
  claims: Claim[] | null;
  pillars: Pillar[];
  brandId: string;
  canApprove: boolean;
  status: "pending" | "approved" | "rejected";
  onStatusChange: (s: "pending" | "approved" | "rejected") => void;
  onChange: () => Promise<void>;
  onNotice: (msg: string) => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});

  async function review(
    claimId: string,
    approvalStatus: "approved" | "rejected" | "pending",
  ) {
    setBusyId(claimId);
    try {
      await api(`/claims/${claimId}`, {
        method: "PATCH",
        body: {
          approvalStatus,
          ...(edits[claimId] ? { text: edits[claimId] } : {}),
        },
      });
      // Say what happened. Otherwise a row filtered out by the current tab just
      // vanishes and the action looks like it failed.
      onNotice(
        approvalStatus === "approved"
          ? "Claim approved and added to the scored baseline."
          : approvalStatus === "rejected"
            ? "Claim rejected and removed from the baseline."
            : "Claim moved back to pending and removed from the baseline.",
      );
      await onChange();
    } finally {
      setBusyId(null);
    }
  }

  async function bulkApprove() {
    const confident = (claims ?? []).filter(
      (c) => c.extractionConfidence >= 0.85,
    );
    if (confident.length === 0) return;
    await api(`/brands/${brandId}/claims/bulk-review`, {
      method: "POST",
      body: {
        claimIds: confident.map((c) => c.claimId),
        approvalStatus: "approved",
      },
    });
    onNotice(`Approved ${confident.length} high-confidence claims.`);
    await onChange();
  }

  const pillarName = (id: string) =>
    pillars.find((p) => p.pillarId === id)?.name ?? "Unknown pillar";
  const confidentCount = (claims ?? []).filter(
    (c) => c.extractionConfidence >= 0.85,
  ).length;

  return (
    <section className="card gt-section" style={{ padding: 0 }}>
      <div
        className="gt-section__head"
        style={{ padding: "var(--space-6) var(--space-6) 0" }}
      >
        <h2 className="gt-section__title">
          Claims{claims && claims.length > 0 ? ` (${claims.length})` : ""}
        </h2>
      </div>

      <DataTable
        rows={claims}
        rowKey={(c) => c.claimId}
        searchPlaceholder="Search claims by text or pillar…"
        pageSize={20}
        toolbar={
          <>
            <div className="gt-filterrow">
              {(["pending", "approved", "rejected"] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`btn btn--sm ${status === value ? "btn--primary" : "btn--ghost"}`}
                  onClick={() => onStatusChange(value)}
                >
                  {value[0]!.toUpperCase() + value.slice(1)}
                </button>
              ))}
            </div>
            {canApprove && status === "pending" && confidentCount > 0 && (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => void bulkApprove()}
              >
                <Icon name="check" size={16} />
                Approve {confidentCount} high-confidence
              </button>
            )}
          </>
        }
        empty={{
          icon: "check",
          title:
            status === "pending"
              ? "Nothing waiting for review"
              : `No ${status} claims`,
          description:
            status === "pending"
              ? "Approved claims form the baseline that drift is measured against. Upload a document to extract more."
              : `Switch to another tab to see claims in a different state.`,
        }}
        // Full text and the edit box live in the expanded row — a claim is a
        // sentence, not a cell, and truncating it in the table keeps the list
        // scannable while the detail stays one click away.
        renderExpanded={(claim) => (
          <div className="gt-claimdetail">
            <textarea
              className="field__input gt-claim__text"
              defaultValue={claim.text}
              rows={3}
              aria-label="Claim text"
              disabled={!canApprove}
              onChange={(e) =>
                setEdits((prev) => ({
                  ...prev,
                  [claim.claimId]: e.target.value,
                }))
              }
            />
            {claim.editedFromOriginal && claim.originalText && (
              <p className="gt-claimdetail__original">
                Model&rsquo;s original wording: &ldquo;{claim.originalText}
                &rdquo;
              </p>
            )}
            {canApprove && (
              <div className="gt-claim__actions">
                {claim.approvalStatus !== "pending" && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busyId === claim.claimId}
                    onClick={() => void review(claim.claimId, "pending")}
                  >
                    Move back to pending
                  </button>
                )}
                {claim.approvalStatus !== "rejected" && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm"
                    disabled={busyId === claim.claimId}
                    onClick={() => void review(claim.claimId, "rejected")}
                  >
                    Reject
                  </button>
                )}
                {claim.approvalStatus !== "approved" && (
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    disabled={busyId === claim.claimId}
                    onClick={() => void review(claim.claimId, "approved")}
                  >
                    <Icon name="check" size={16} />
                    Approve
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        columns={[
          {
            key: "text",
            header: "Claim",
            value: (c) => c.text,
            render: (c) => <span className="gt-claimcell">{c.text}</span>,
          },
          {
            key: "pillar",
            header: "Pillar",
            value: (c) => pillarName(c.pillarId),
            width: "22%",
            render: (c) => (
              <span className="badge badge--accent">
                {pillarName(c.pillarId)}
              </span>
            ),
          },
          {
            key: "status",
            header: "Status",
            width: "11%",
            value: (c) => c.approvalStatus,
            render: (c) => (
              <span
                className={`badge ${
                  c.approvalStatus === "approved"
                    ? "badge--success"
                    : c.approvalStatus === "rejected"
                      ? "badge--error"
                      : "badge--warning"
                }`}
              >
                {c.approvalStatus}
              </span>
            ),
          },
          {
            key: "confidence",
            header: "Confidence",
            align: "right",
            width: "12%",
            value: (c) => c.extractionConfidence,
            render: (c) => (
              // Low confidence is where a reviewer's attention is worth most
              // (PRODUCT_FLOW Stage 2 §3), so sorting by it is the point.
              <span
                className={`badge ${c.extractionConfidence >= 0.85 ? "badge--success" : "badge--warning"}`}
              >
                {Math.round(c.extractionConfidence * 100)}%
              </span>
            ),
          },
        ]}
      />
    </section>
  );
}

/* ------------------------------ Ask Echo ------------------------------- */

function AskEchoSection({
  brandId,
  pillars,
}: {
  brandId: string;
  pillars: Pillar[];
}) {
  const [question, setQuestion] = useState("");
  const [pillarId, setPillarId] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<{
    answer: string;
    sufficient: boolean;
    gap: string | null;
    citedClaims: {
      claimId: string;
      text: string;
      pillarName: string;
      documentTitle: string;
      sourceUrl: string | null;
      sourceType: string;
      duplicateOf: number;
    }[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function ask(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      setAnswer(
        await api(`/brands/${brandId}/rag/ask`, {
          method: "POST",
          body: { question, ...(pillarId ? { pillarId } : {}) },
        }),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not answer that.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card gt-section">
      <div className="gt-section__head">
        <h2 className="gt-section__title">Ask your Ground Truth</h2>
      </div>
      <p className="gt-section__lede">
        What would our official answer be, using only approved claims? This
        never leaves Echo — it is the baseline the engines get compared against,
        not something they are ever shown.
      </p>

      <form onSubmit={ask} noValidate>
        <div className="gt-uploadgrid">
          <Field
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Has the company reached net zero?"
            required
          />
          <SelectField
            label="Limit to pillar"
            value={pillarId}
            onChange={(e) => setPillarId(e.target.value)}
          >
            <option value="">All pillars</option>
            {pillars.map((p) => (
              <option key={p.pillarId} value={p.pillarId}>
                {p.name}
              </option>
            ))}
          </SelectField>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={busy || question.length < 5}
          >
            {busy ? "Thinking…" : "Ask"}
          </button>
        </div>
      </form>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {answer && (
        <div className="gt-answer">
          {!answer.sufficient && (
            <div className="alert alert--warning" role="status">
              <Icon name="alert" size={20} />
              <span>
                {answer.gap ??
                  "The approved claims do not fully cover this question."}
              </span>
            </div>
          )}
          <p className="gt-answer__text">{answer.answer}</p>
          {answer.citedClaims.length > 0 && (
            <>
              <h3 className="gt-answer__heading">
                Grounded in {answer.citedClaims.length} approved claim
                {answer.citedClaims.length === 1 ? "" : "s"}
              </h3>
              {/* Each claim carries the document it came from, so the trail
                  ends at a source a sceptical reader can open — not at a
                  sentence with no origin. */}
              <ul className="gt-evidence">
                {answer.citedClaims.map((c) => (
                  <li key={c.claimId} className="gt-evidence__item">
                    <p className="gt-evidence__claim">{c.text}</p>
                    <p className="gt-evidence__source">
                      <span className="badge badge--accent">
                        {c.pillarName}
                      </span>
                      {c.sourceUrl ? (
                        <a
                          href={c.sourceUrl}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="auth__link"
                        >
                          {c.documentTitle}
                        </a>
                      ) : (
                        <span>{c.documentTitle}</span>
                      )}
                      <span className="gt-evidence__type">
                        {SOURCE_TYPES.find(([v]) => v === c.sourceType)?.[1] ??
                          c.sourceType}
                      </span>
                      {c.duplicateOf > 1 && (
                        <span
                          className="badge badge--neutral"
                          title="This claim was extracted from the same source under more than one pillar"
                        >
                          ×{c.duplicateOf} pillars
                        </span>
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
